import { useCallback } from 'react'

import { isAxiosError } from 'axios'
import { useUnit } from 'effector-react'
import { useMutation, useQueryClient } from 'react-query'

import { useToast } from '@/components/ui/toast.tsx'
import { DIALOG_TYPE } from '@/dialogs/constants'
import { $creatorId } from '@/models/creators.ts'
import { openDialog } from '@/models/dialogs'
import type { MessageSendRequest } from '@/modules/chatting/types.ts'
import { getErrorMessage, handleError } from '@/utils/error'

import { massMessagesService } from '../service/mass-message'
import { getBlacklistedWordMatch } from '../utils/blacklistedWords'

export function useSendMassMessage({ onSuccess }: { onSuccess: () => void }) {
  const queryClient = useQueryClient()
  const creatorId = useUnit($creatorId)

  const toast = useToast()

  const sendMessageMutation = useMutation(
    (message: Partial<MessageSendRequest>) => {
      return massMessagesService.sendMassMessage(creatorId!, message)
    },
    {
      onSuccess: () => {
        toast.success('Message sent successfully')
        onSuccess()

        queryClient.invalidateQueries(['messagesQueue'])
      },
      onError: (error) => {
        if (!isAxiosError(error)) {
          sendMessageMutation.reset()

          return
        }

        const blacklistedWordDetection = getBlacklistedWordMatch(
          getErrorMessage(error),
        )

        if (blacklistedWordDetection) {
          openDialog({
            type: DIALOG_TYPE.BLACKLISTED_WORD_USAGE_DETECTION,
            match: blacklistedWordDetection,
          })
        } else {
          handleError(
            error,
            'Unexpected error occurred while sending the mass message, please try again later',
          )
        }
      },
    },
  )
  const sendMassMessage = useCallback(
    async ({
      totalRecepientsCount,
      ...message
    }: Partial<MessageSendRequest> & { totalRecepientsCount: number }) => {
      openDialog({
        type: DIALOG_TYPE.CONFIRMATION,
        title: `Please confirm you want to send this message to ${totalRecepientsCount} recipients`,
        onConfirm: () => sendMessageMutation.mutateAsync(message),
      })
    },
    [sendMessageMutation],
  )

  return {
    sendMassMessage,
    isSendMassMessageLoading: sendMessageMutation.isLoading,
  }
}
